import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client';
import { useSearchParams } from 'next/navigation';
import { LocaleUtils } from 'react-day-picker';
import clsx from 'clsx';
import {
  Button,
  Calendar,
  DropDownModal,
  Heading,
  InlineMessages,
  InlineMessagesType,
  Types,
  getCurrentDateObject,
} from '@marriott/mi-ui-library';
import {
  ActiveStatus,
  AddHotelDttInput,
  AddHotelDttResponse,
  addHotelCorporateDttQuery,
  addHotelGroupDttQuery,
} from '@marriott/mi-groups-graphql';
import { PropertyCard } from './PropertyCard';
import { ReslinkPropertyCard } from '../ReslinkEventDetails.types';
import { TextFormField } from '../../../../../molecules';
import { DateObject } from '../../../../../molecules/DatePicker/DatePicker.types';
import {
  CALENDAR_SHORT_WEEKDAY_NAMES,
  CALENDAR_LONG_WEEKDAY_NAMES,
  CALENDAR_SHORT_MONTH_NAMES,
  CALENDAR_MAX_ALLOWED_DAYS,
  CALENDAR_START_DATE,
  ENTER_KEY,
  ALPHA_NUMERIC_REGEX,
  PROPERTY_IMAGE_LIST_CATEGORY_LIMIT,
  RESLINK_MAX_PROPERTY_COUNT,
  RESLINK_MAX_ROOM_COUNT,
} from '../../../../../constants';
import {
  formatUTCDateString,
  getCurrentLocale,
  getFormattedCalendarDate,
  getFormattedDate,
  getFormattedDateObject,
  getLocaleValue,
  isPastDate,
  setCalendarOptions,
} from '../../../../../utils';
import { AddHotelProps, AddHotelDttForm, CalendarState } from './AddHotel.types';
import { StyledAddHotelForm } from './AddHotel.styles';
import { ModifyDatesOccupancyModal } from './ModifyDatesOccupancyModal';
import { ModifyDatesOccupancyData } from './ModifyDatesOccupancyModal/ModifyDatesOccupancyModal.types';
import { ReservationLinkType } from '../../DashboardContentTiles/ReslinkTypeModal/ReslinkTypeModal.types';
import { useLocaleStore } from '../../../../../store';
import { useReslinkAssociateStore } from '../../../../../admin/store';
import {
  getPropertyDataOnAddHotel,
  isGroupType,
  isCorporateType,
  checkMarshaProperty,
  hasMissingInventoryDtt,
  getEventTrackingDataDtt,
  getMissingInventoryDatesDtt,
} from '../../../../utils/reslink';
import { useFocusOut } from '../../../../../hooks';
import { InventoryCheckData, UpdatedInventoryData } from './InventoryCheckModal/InventoryCheckModal.types';
import { InventoryCheckModal } from './InventoryCheckModal';

export const AddHotelDtt: FC<AddHotelProps> = (props: AddHotelProps) => {
  const { labels, control, errors, onError, isPickUpStatsEnabled } = props;
  const {
    heading,
    helperText,
    propertyId,
    groupCode,
    miniHotelStartDate,
    corporateId,
    cta,
    removeHotelPopUp,
    propertyCard: propertyCardLabels,
    editHotelPopUp,
    maxRoomCardError,
    apiFailure,
    inventoryCheckPopUp,
  } = labels;
  const {
    shortWeekdayNames = CALENDAR_SHORT_WEEKDAY_NAMES,
    longWeekdayNames = CALENDAR_LONG_WEEKDAY_NAMES,
    shortMonthNames = CALENDAR_SHORT_MONTH_NAMES,
    longMonthNames = LocaleUtils.getMonths(),
  } = editHotelPopUp.dateLabels;
  const { cancelCtaLabel, confirmCtaLabel } = cta;

  const urlParams = useSearchParams();
  const reslinkType = urlParams.get('reslinkType') || '';

  const isGroup = isGroupType(reslinkType as ReservationLinkType);
  const isCorporate = isCorporateType(reslinkType as ReservationLinkType);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const calendarRef = useRef<any>(null);
  const datePickerInputRef = useRef<HTMLDivElement>(null);
  const dropDownRef = useRef<HTMLDivElement>(null);

  const { locale } = useLocaleStore();
  const { setEventTrackingData, setEventTrackingPropertyIndex, eventTrackingPropertyIndex } =
    useReslinkAssociateStore();

  const [calendarState, setCalendarState] = useState<CalendarState>({
    startDate: null,
    isFlyoutOpen: false,
  });
  const [showModifyDatesOccupancyModal, setShowModifyDatesOccupancyModal] = useState<boolean>(false);
  const [modifyDatesOccupancyData, setModifyDatesOccupancyData] = useState<ModifyDatesOccupancyData>({
    id: '',
    startDate: '',
    endDate: '',
    overriddenStartDate: '',
    overriddenEndDate: '',
    maxGuestsPerRoom: '',
  });
  const [apiError, setApiError] = useState(false);
  const [currentlyAddedHotel, setCurrentlyAddedHotel] = useState<AddHotelDttResponse>();
  const [showInventoryCheckModal, setShowInventoryCheckModal] = useState(false);
  const [inventoryCheckCount, setInventoryCheckCount] = useState(0);
  const [inventoryCheckData, setInventoryCheckData] = useState<InventoryCheckData>({
    startDate: '',
    endDate: '',
  });

  const {
    control: addHotelControl,
    trigger,
    getValues,
    setValue,
    reset,
    formState: { errors: addHotelErrors },
  } = useForm<AddHotelDttForm>({
    defaultValues: {
      propertyId: '',
      groupCode: '',
      miniHotelStartDate: '',
      corporateId: '',
    },
  });

  const {
    fields: propertyCardsData,
    append,
    update,
    remove,
    swap,
  } = useFieldArray({
    name: 'propertyCards',
    control,
  });

  useEffect(() => {
    setCalendarOptions({
      shortWeekdayNames,
      longWeekdayNames,
      shortMonthNames,
      longMonthNames,
    });
  }, [shortWeekdayNames, longWeekdayNames, shortMonthNames, longMonthNames]);

  useEffect(() => {
    setCalendarState(prevState => ({ ...prevState, startDate: getCurrentDateObject() }));
  }, []);

  useFocusOut({
    ref: [datePickerInputRef.current, dropDownRef.current],
    onFocusOut: (isFocusOut: boolean) => {
      if (isFocusOut) {
        closeFlyout();
      }
    },
    handleKeyup: true,
  });

  const [addHotel, { data: addHotelResponse, loading, error: addHotelError }] = useLazyQuery<AddHotelDttResponse>(
    isGroup ? addHotelGroupDttQuery : addHotelCorporateDttQuery
  );

  const updateStartDate = (startDate: DateObject | null) => {
    setCalendarState(prevState => ({ ...prevState, startDate }));
    setValue('miniHotelStartDate', getFormattedDate(startDate));
    closeFlyout();
  };

  const toggleFlyout = () => setCalendarState(prevState => ({ ...prevState, isFlyoutOpen: !prevState.isFlyoutOpen }));

  const closeFlyout = () => setCalendarState(prevState => ({ ...prevState, isFlyoutOpen: false }));

  const handleAddHotel = async () => {
    setApiError(false);
    const fields: ('propertyId' | 'groupCode' | 'corporateId')[] = isGroup
      ? ['propertyId', 'groupCode']
      : ['propertyId', 'corporateId'];
    const isValid = await trigger(fields);

    if (isValid) {
      const newPropertyId = getValues('propertyId').toUpperCase();
      const addHotelInput: AddHotelDttInput = {
        propertyId: newPropertyId,
        imageLimitPerCategory: PROPERTY_IMAGE_LIST_CATEGORY_LIMIT,
        limit: 1,
      };

      if (isGroup) {
        addHotelInput.input = {
          propertyId: newPropertyId,
          filters: {
            groupCode: getValues('groupCode').toUpperCase(),
          },
          activeStatus: ActiveStatus.ACTIVE,
          groupBlockCodeExactMatch: true,
          eventLookupDateRange: {
            startDate: getFormattedCalendarDate(
              getValues('miniHotelStartDate') || getFormattedDate(getCurrentDateObject()),
              'hyphenatedDateWithMonthNoAndYear'
            ),
          },
        };
      }

      addHotel({
        variables: addHotelInput,
        context: {
          headers: {
            'accept-language': locale,
            ...(isGroup && { 'uxl-cache-disable': true }),
          },
        },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      });
    }
  };

  useEffect(() => {
    if (!addHotelResponse && !addHotelError) return;
    const hasSingleError = addHotelError?.graphQLErrors.length === 1;
    const inventoryStaticsError =
      addHotelError && addHotelError?.graphQLErrors.some(e => e.path?.includes('inventoryStatistics'));
    const roomTypeError = addHotelError && addHotelError?.graphQLErrors.some(e => e.path?.includes('roomTypeDetails'));

    const isInventoryErrorOnly = addHotelError && hasSingleError && inventoryStaticsError;
    const isSuccessfulResponse = !addHotelError && !!addHotelResponse;

    if (isInventoryErrorOnly || roomTypeError || isSuccessfulResponse) {
      onError('');
      const { groupsMultipleGroupEventsAtProperty } = addHotelResponse?.hotelOperations?.groupManagement || {};
      if (
        !addHotelResponse?.property.basicInformation ||
        (isGroup && !groupsMultipleGroupEventsAtProperty?.groupBlocks?.length)
      ) {
        window.scrollTo(0, 0);
        onError(propertyId.propertyIdError);
      } else if (
        isGroup &&
        inventoryCheckPopUp.isInventoryCheckEnabled &&
        groupsMultipleGroupEventsAtProperty?.groupBlocks?.length &&
        !inventoryStaticsError
      ) {
        checkInventoryAvailability(addHotelResponse);
      } else {
        displayHotel(addHotelResponse);
      }
    } else {
      window.scrollTo(0, 0);
      onError(isGroup ? propertyId.propertyIdError : corporateId.corporateIdError);
    }
  }, [addHotelResponse]);

  useEffect(() => {
    if (!!addHotelError && !addHotelResponse) {
      if (addHotelError?.networkError) {
        setApiError(true);
      } else {
        window.scrollTo(0, 0);
        onError(isGroup ? propertyId.propertyIdError : corporateId.corporateIdError);
      }
    }
  }, [addHotelError, addHotelResponse]);

  const handleMoveUp = (index: number) => swap(index, index - 1);

  const handleMoveDown = (index: number) => swap(index, index + 1);

  const handleEdit = (propertyData: ReslinkPropertyCard & { id: string }) => {
    setModifyDatesOccupancyData({
      id: propertyData.id,
      startDate: propertyData?.fromDate || '',
      endDate: propertyData?.toDate || '',
      overriddenStartDate: propertyData?.overriddenStartDate,
      overriddenEndDate: propertyData?.overriddenEndDate,
      maxGuestsPerRoom:
        propertyData?.overriddenMaxOccupancy?.toString() || propertyData?.maxOccupancy?.toString() || '',
    });
    setShowModifyDatesOccupancyModal(true);
  };

  const updateDateAndOccupancy = ({
    id,
    overriddenStartDate,
    overriddenEndDate,
    maxGuestsPerRoom,
  }: ModifyDatesOccupancyData) => {
    const propertyIndex = propertyCardsData.findIndex(item => item?.id === id);

    if (propertyIndex !== -1) {
      const property = propertyCardsData[propertyIndex];

      update(propertyIndex, {
        ...property,
        overriddenStartDate,
        overriddenEndDate,
        overriddenMaxOccupancy: +maxGuestsPerRoom || (property?.overriddenMaxOccupancy as number),
        isEdited: true,
      });
    }
  };

  const propertyCardValidator = (property: ReslinkPropertyCard) => {
    if (isGroup) {
      const roomsCount = property.roomsData.filter(roomData => !roomData.isRemoved).length;
      const isMarshaProperty = property.roomsData.some(roomData => roomData.groupCode);
      const isPastDatedEvent = isPastDate(property?.fromDate || '') && isPastDate(property?.toDate || '');
      if (roomsCount === 0 || isPastDatedEvent) return propertyCardLabels.noInventoryCodeError;
      if (isMarshaProperty && roomsCount > RESLINK_MAX_ROOM_COUNT) return maxRoomCardError;
    }
    return true;
  };

  const checkInventoryAvailability = (addHotelResponse: AddHotelDttResponse) => {
    const groupBlock =
      addHotelResponse.hotelOperations?.groupManagement.groupsMultipleGroupEventsAtProperty.groupBlocks?.[0];
    const timespans = groupBlock?.groupContract.timespans || [];
    const eventLookupDateRange = {
      startDate: formatUTCDateString(timespans?.[0].start),
      endDate: formatUTCDateString(timespans?.[0].end),
    };
    const hasMissingInventory = hasMissingInventoryDtt(groupBlock?.inventoryStatistics?.groupByGroupPeriods ?? []);

    if (hasMissingInventory) {
      const missingInventoryDates = getMissingInventoryDatesDtt(
        groupBlock?.inventoryStatistics?.groupByGroupPeriods ?? []
      );
      updateInventoryCheck(addHotelResponse, { ...eventLookupDateRange, missingInventoryDates });
    } else {
      displayHotel(addHotelResponse);
    }
  };

  const updateInventoryCheck = (addHotelResponse: AddHotelDttResponse, timespans: InventoryCheckData) => {
    setInventoryCheckData(timespans);
    setCurrentlyAddedHotel(addHotelResponse);
    setShowInventoryCheckModal(true);
    setInventoryCheckCount(count => count + 1);
  };

  const updateInventoryDateRange = ({ overriddenStartDate, overriddenEndDate }: UpdatedInventoryData) => {
    const updatedData: AddHotelDttResponse = {
      ...currentlyAddedHotel!,
    };
    setCurrentlyAddedHotel(updatedData);
    setShowInventoryCheckModal(false);
    displayHotel(updatedData, { overriddenStartDate, overriddenEndDate });
  };

  const handleCloseInventoryModal = () => {
    setShowInventoryCheckModal(false);
    getValues('groupCode') && displayHotel(currentlyAddedHotel!);
  };

  const displayHotel = (addHotelResponse: AddHotelDttResponse, updatedInventoryData?: UpdatedInventoryData) => {
    const miniHotelStartDate =
      isGroup && getValues('miniHotelStartDate')
        ? getFormattedDate(
          getFormattedDateObject(getValues('miniHotelStartDate'), 'dateWithDayMonthAndYear'),
          'hyphenatedDateWithMonthNoAndYear'
        )
        : '';

    const propertyIndex = eventTrackingPropertyIndex + 1;

    append({
      ...getPropertyDataOnAddHotel(
        { ...addHotelResponse, miniHotelStartDate, groupCode: getValues('groupCode').toUpperCase() },
        propertyCardLabels,
        propertyIndex
      ),
      propertyId: getValues('propertyId').toUpperCase() || '',
      groupBlockCode: isGroup ? getValues('groupCode').toUpperCase() : undefined,
      corporateId: isCorporate ? getValues('corporateId').toUpperCase() : undefined,
      overriddenStartDate: updatedInventoryData?.overriddenStartDate || '',
      overriddenEndDate: updatedInventoryData?.overriddenEndDate || '',
    });

    setEventTrackingPropertyIndex(propertyIndex);

    const groupBlock =
      addHotelResponse?.hotelOperations?.groupManagement?.groupsMultipleGroupEventsAtProperty?.groupBlocks?.[0];
    if (groupBlock) {
      const { groupContract, products, sourceCRS, inventoryStatistics } = groupBlock;
      const { groupByGroupPeriods, groupByProductPeriods } = inventoryStatistics || {};
      const isHousingProtected = groupContract?.derivedFields?.isHousingProtected;

      if (isPickUpStatsEnabled && !isHousingProtected) {
        const eventData = getEventTrackingDataDtt(
          addHotelResponse?.property?.basicInformation?.name || '',
          propertyIndex,
          groupByGroupPeriods,
          groupContract?.pricePlans?.[0]?.priceGridDetails?.amountRules,
          products,
          groupByProductPeriods,
          groupContract?.code,
          sourceCRS
        );

        setEventTrackingData(eventData);
      }
    }

    reset();
  };

  const propertyIdField = (
    <Controller
      name="propertyId"
      control={addHotelControl}
      rules={{
        required: propertyId.requiredError,
        pattern: {
          value: ALPHA_NUMERIC_REGEX,
          message: propertyId.invalidError,
        },
        validate: value => (value.length < 5 ? propertyId.lengthError : true),
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={propertyId.label}
          ariaLabel={propertyId.label}
          placeholder={propertyId.placeholder}
          maxLength={5}
          className="m-input-field"
          showErrorMessage={!!addHotelErrors?.propertyId?.message}
          errorMessage={addHotelErrors?.propertyId?.message}
        />
      )}
    />
  );

  const groupCodeField = (
    <Controller
      name="groupCode"
      control={addHotelControl}
      rules={{
        required: groupCode.requiredError,
        pattern: {
          value: ALPHA_NUMERIC_REGEX,
          message: groupCode.invalidError,
        },
        validate: value => (value.length >= 3 && value.length <= 10 ? true : groupCode.lengthError),
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={groupCode.label}
          ariaLabel={groupCode.label}
          placeholder={groupCode.placeholder}
          maxLength={10}
          className="m-input-field"
          showErrorMessage={!!addHotelErrors?.groupCode?.message}
          errorMessage={addHotelErrors?.groupCode?.message}
        />
      )}
    />
  );

  const miniHotelStartDateField = (
    <Controller
      name="miniHotelStartDate"
      control={addHotelControl}
      render={({ field }) => (
        <div ref={datePickerInputRef}>
          <TextFormField
            {...field}
            label={miniHotelStartDate.label}
            ariaLabel={miniHotelStartDate.label}
            className="m-input-field"
            showIcon={true}
            iconClass="icon icon-calendar"
            iconAriaLabel="calender icon"
            isIconClickable={true}
            iconClickHandler={toggleFlyout}
            showHelperText={!calendarState.isFlyoutOpen && !!miniHotelStartDate.warningMessage}
            helperText={miniHotelStartDate.warningMessage}
            onClick={toggleFlyout}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === ENTER_KEY) {
                event.preventDefault();
                toggleFlyout();
              }
            }}
            onChange={event => {
              field.onChange(event);
            }}
          />
        </div>
      )}
    />
  );

  const calendar = (
    <Calendar
      ref={calendarRef}
      startDate={null}
      endDate={null}
      variation=""
      focusedInput={CALENDAR_START_DATE}
      weekdaysShort={shortWeekdayNames}
      weekdays={shortWeekdayNames}
      weekdaysLong={longWeekdayNames}
      monthsShort={shortMonthNames}
      months={longMonthNames}
      numberOfMonths={1}
      firstDayOfWeek={getLocaleValue('firstDayofWeek')}
      maxAllowedDays={CALENDAR_MAX_ALLOWED_DAYS}
      yearLabel={getLocaleValue('year') || ''}
      nightLabel=""
      nightsLabel=""
      singleDatePicker={true}
      startFromToday={true}
      disableHoverToolTip={true}
      setDateHandler={startDate => updateStartDate(startDate)}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setFocusedInputHandler={() => { }}
      locale={getCurrentLocale()}
    />
  );

  const corporateIdField = (
    <Controller
      name="corporateId"
      control={addHotelControl}
      rules={{
        required: corporateId.requiredError,
        pattern: {
          value: ALPHA_NUMERIC_REGEX,
          message: corporateId.invalidError,
        },
        validate: value => (value.length >= 3 && value.length <= 10 ? true : corporateId.lengthError),
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={corporateId.label}
          ariaLabel={corporateId.label}
          placeholder={corporateId.placeholder}
          maxLength={10}
          className="m-input-field"
          showErrorMessage={!!addHotelErrors?.corporateId?.message}
          errorMessage={addHotelErrors?.corporateId?.message}
        />
      )}
    />
  );

  const groupAddHotelForm = (
    <>
      <div className="col-lg-3 col-md-6 pb-5">{propertyIdField}</div>
      <div className="col-lg-3 col-md-6 pb-5">{groupCodeField}</div>
      <div className="col-lg-3 col-md-6 pb-5">
        {miniHotelStartDateField}
        <div ref={dropDownRef}>
          <DropDownModal
            show={calendarState.isFlyoutOpen}
            handleBlur={true}
            className="calendar-modal"
            dropdownModalOpenState={calendarState.isFlyoutOpen}
            setDropdownModalOpenState={closeFlyout}
            dropdownModalOnCLoseFunc={closeFlyout}
          >
            {calendarState.startDate && calendar}
          </DropDownModal>
        </div>
      </div>
    </>
  );

  const corporateAddHotelForm = (
    <>
      <div className="col-md-3 pb-5">{propertyIdField}</div>
      <div className="col-md-3 pb-5">{corporateIdField}</div>
      <div className="col-md-3 pb-5"></div>
    </>
  );

  return (
    <div>
      <Heading
        variation={Types.headingType.title}
        titleText={heading}
        fontSize={Types.size.medium}
        element={Types.tags.h3}
        customClass="mt-5"
      />
      <p className="t-font-alt-s mt-2">{helperText}</p>

      <StyledAddHotelForm>
        <div className="row">
          {isGroup ? groupAddHotelForm : corporateAddHotelForm}
          <div className="col-md-3 d-flex flex-column">
            <Button
              type={Types.ButtonTypeVariation.Button}
              className={clsx(
                'm-button-secondary add-hotel-cta',
                loading && 'm-button skeleton-loader',
                propertyCardsData.length >= RESLINK_MAX_PROPERTY_COUNT && 'disabled'
              )}
              isDisabled={loading || propertyCardsData.length >= RESLINK_MAX_PROPERTY_COUNT}
              callback={handleAddHotel}
            >
              {cta.addHotelCtaLabel}
            </Button>
            <p className="t-font-alt-s text-center mb-0 mt-3">
              {propertyCardsData.length >= RESLINK_MAX_PROPERTY_COUNT
                ? cta.propertyLimitHelperText
                : cta.addHotelHelperText.replace(
                  '{hotelNumber}',
                  `${RESLINK_MAX_PROPERTY_COUNT - propertyCardsData.length}`
                )}
            </p>
          </div>
        </div>
      </StyledAddHotelForm>

      {apiError ? (
        <div className="mt-3">
          <InlineMessages type={InlineMessagesType.Error} title={apiFailure} severity="1" />
        </div>
      ) : null}

      {propertyCardsData.length
        ? propertyCardsData.map((propertyData, index) => {
          return (
            <div className="mt-3" key={propertyData.id}>
              <Controller
                control={control}
                name={`propertyCards.${index}`}
                rules={{
                  validate: propertyCardValidator,
                }}
                render={() => (
                  <PropertyCard
                    propertyLabels={{
                      ...propertyCardLabels,
                      apiFailure,
                    }}
                    propertyData={{
                      ...propertyData,
                      cardNumber: index + 1,
                      totalCards: propertyCardsData.length,
                      reslinkType: reslinkType as ReservationLinkType,
                    }}
                    hasError={
                      !!errors?.propertyCards?.[index] ||
                      (isGroup &&
                        (!propertyData?.roomsData?.length ||
                          (isPastDate(propertyData?.fromDate || '') && isPastDate(propertyData?.toDate || ''))))
                    }
                    onEdit={() => handleEdit(propertyData)}
                    remove={remove}
                    update={update}
                    removeHotelLabels={{ ...removeHotelPopUp, cancelCtaLabel, confirmCtaLabel }}
                    onMoveUp={() => handleMoveUp(index)}
                    onMoveDown={() => handleMoveDown(index)}
                  />
                )}
              />
            </div>
          );
        })
        : null}

      {showModifyDatesOccupancyModal && (
        <ModifyDatesOccupancyModal
          show={showModifyDatesOccupancyModal}
          labels={editHotelPopUp}
          defaultValues={modifyDatesOccupancyData}
          closeModal={() => setShowModifyDatesOccupancyModal(false)}
          onModify={updatedDateAndOccupancy => updateDateAndOccupancy(updatedDateAndOccupancy)}
        />
      )}
      {showInventoryCheckModal && (
        <InventoryCheckModal
          show={showInventoryCheckModal}
          labels={inventoryCheckPopUp}
          defaultValues={inventoryCheckData}
          analyticsCount={inventoryCheckCount}
          closeModal={handleCloseInventoryModal}
          onConfirmInventoryCheck={updateInventoryDateRange}
          isMarshaProperty={checkMarshaProperty(
            currentlyAddedHotel?.hotelOperations?.groupManagement.groupsMultipleGroupEventsAtProperty.groupBlocks?.[0]
              ?.sourceCRS
          )}
        />
      )}
    </div>
  );
};
